import { createReducer } from '__common/utils/helpers';
import { LiteProjectActionTypes } from 'actionsConstants';
import { BomActionTypes } from 'bom/bomActionsConstants';
import type { UserProfile } from '../../../../decls/bom/userProfile';

export type LiteProjectState = {
  creating: boolean;
  updating: boolean;
  error: boolean;
  projectId: string | null;
  lastSaveAt: number | null;
  userProfiles: UserProfile[];
  userProfilesLoading: boolean;
  profileSaving: boolean;
  // Most recent SAVE_USER_PROFILE_SUCCESS payload — captured so the design
  // workspace can chain an UPDATE_LITE_PROJECT_REQUEST that persists the new
  // profile's id as a ProjectVariable on the project the profile was created
  // for. `id` is null when the backend response omitted the profile primary
  // key; `projectId` is null when the profile was saved outside a project
  // context.
  lastCreatedProfile: { id: number | null; title: string | null; projectId: string | null } | null;
  // Project-scoped ProjectVariable values, fetched separately from the
  // FETCH_PROJECT response because the default project serializer doesn't
  // surface ProjectVariable rows. Populated on workspace mount via
  // FETCH_PROJECT_VARIABLES_REQUEST. Reset on each new fetch.
  projectVariables: { [name: string]: string };
  // True from FETCH_LITE_BOM_REQUEST until FETCH_BOM_PART_LIST_SUCCESS/ERROR
  // or BOM_RESET. Bridges the gap between the autosave PUT completing
  // (updating=false) and the chained GET BOM completing — without it the
  // right-panel overlay drops as soon as the PUT succeeds, then the user
  // sees stale parts until the GET returns a few hundred ms later.
  bomRefreshing: boolean;
};

export const liteProjectInitialState: LiteProjectState = {
  creating: false,
  updating: false,
  error: false,
  projectId: null,
  lastSaveAt: null,
  userProfiles: [],
  userProfilesLoading: false,
  profileSaving: false,
  lastCreatedProfile: null,
  projectVariables: {},
  bomRefreshing: false,
};

export default {
  liteProject: createReducer(liteProjectInitialState, {
    [LiteProjectActionTypes.CREATE_LITE_PROJECT_REQUEST](state: LiteProjectState): LiteProjectState {
      return { ...state, creating: true, error: false };
    },
    [LiteProjectActionTypes.CREATE_LITE_PROJECT_SUCCESS](state: LiteProjectState, action: any): LiteProjectState {
      return {
        ...state,
        creating: false,
        lastSaveAt: Date.now(),
        projectId: (action.payload && action.payload.projectId) || state.projectId,
      };
    },
    [LiteProjectActionTypes.CREATE_LITE_PROJECT_FAILURE](state: LiteProjectState): LiteProjectState {
      return { ...state, creating: false, error: true };
    },
    [LiteProjectActionTypes.UPDATE_LITE_PROJECT_REQUEST](state: LiteProjectState): LiteProjectState {
      return { ...state, updating: true, error: false };
    },
    [LiteProjectActionTypes.UPDATE_LITE_PROJECT_SUCCESS](state: LiteProjectState, action: any): LiteProjectState {
      return {
        ...state,
        updating: false,
        lastSaveAt: (action.payload && action.payload.lastSaveAt) || Date.now(),
      };
    },
    [LiteProjectActionTypes.UPDATE_LITE_PROJECT_FAILURE](state: LiteProjectState): LiteProjectState {
      // PUT failed → no chained GET BOM will fire. Clear any lingering
      // refresh flag so the overlay doesn't hang indefinitely.
      return { ...state, updating: false, error: true, bomRefreshing: false };
    },
    [LiteProjectActionTypes.SAVE_USER_PROFILE_REQUEST](state: LiteProjectState): LiteProjectState {
      return { ...state, profileSaving: true, error: false };
    },
    [LiteProjectActionTypes.SAVE_USER_PROFILE_SUCCESS](state: LiteProjectState, action: any): LiteProjectState {
      const created: UserProfile | undefined = action.payload && action.payload.profile;
      const next = created ? [...state.userProfiles, created] : state.userProfiles;
      const lastCreated = created
        ? {
          id: typeof created.id === 'number' ? created.id : null,
          title: typeof created.title === 'string' ? created.title : null,
          projectId: (action.payload && action.payload.projectId) || null,
        }
        : state.lastCreatedProfile;
      return { ...state, profileSaving: false, userProfiles: next, lastCreatedProfile: lastCreated };
    },
    [LiteProjectActionTypes.SAVE_USER_PROFILE_FAILURE](state: LiteProjectState): LiteProjectState {
      return { ...state, profileSaving: false, error: true };
    },
    [LiteProjectActionTypes.UPDATE_USER_PROFILE_REQUEST](state: LiteProjectState): LiteProjectState {
      return { ...state, profileSaving: true, error: false };
    },
    [LiteProjectActionTypes.UPDATE_USER_PROFILE_SUCCESS](state: LiteProjectState, action: any): LiteProjectState {
      const updated: UserProfile | undefined = action.payload && action.payload.profile;
      if (!updated || typeof updated.id !== 'number') {
        return { ...state, profileSaving: false };
      }
      const updatedId = updated.id;
      const next = state.userProfiles.map((p) => (p && p.id === updatedId ? { ...p, ...updated } : p));
      return { ...state, profileSaving: false, userProfiles: next };
    },
    [LiteProjectActionTypes.UPDATE_USER_PROFILE_FAILURE](state: LiteProjectState): LiteProjectState {
      return { ...state, profileSaving: false, error: true };
    },
    [LiteProjectActionTypes.DELETE_USER_PROFILE_REQUEST](state: LiteProjectState): LiteProjectState {
      return { ...state, profileSaving: true, error: false };
    },
    [LiteProjectActionTypes.DELETE_USER_PROFILE_SUCCESS](state: LiteProjectState, action: any): LiteProjectState {
      const deletedId = action.payload && action.payload.profileId;
      const next = state.userProfiles.filter((p) => p && p.id !== deletedId);
      const lastCreatedProfile = state.lastCreatedProfile && state.lastCreatedProfile.id === deletedId
        ? null
        : state.lastCreatedProfile;
      return { ...state, profileSaving: false, userProfiles: next, lastCreatedProfile };
    },
    [LiteProjectActionTypes.DELETE_USER_PROFILE_FAILURE](state: LiteProjectState): LiteProjectState {
      return { ...state, profileSaving: false, error: true };
    },
    [LiteProjectActionTypes.FETCH_USER_PROFILES_REQUEST](state: LiteProjectState): LiteProjectState {
      return { ...state, userProfilesLoading: true, error: false };
    },
    [LiteProjectActionTypes.FETCH_USER_PROFILES_SUCCESS](state: LiteProjectState, action: any): LiteProjectState {
      const list: UserProfile[] = (action.payload && action.payload.profiles) || [];
      return { ...state, userProfilesLoading: false, userProfiles: list };
    },
    [LiteProjectActionTypes.FETCH_USER_PROFILES_FAILURE](state: LiteProjectState): LiteProjectState {
      return { ...state, userProfilesLoading: false, error: true };
    },
    [LiteProjectActionTypes.FETCH_PROJECT_VARIABLES_REQUEST](state: LiteProjectState): LiteProjectState {
      return { ...state, projectVariables: {} };
    },
    [LiteProjectActionTypes.FETCH_PROJECT_VARIABLES_SUCCESS](state: LiteProjectState, action: any): LiteProjectState {
      const vars = (action.payload && action.payload.variables) || {};
      return { ...state, projectVariables: vars };
    },
    [LiteProjectActionTypes.FETCH_PROJECT_VARIABLES_FAILURE](state: LiteProjectState): LiteProjectState {
      return { ...state, projectVariables: {} };
    },
    // BOM POST shares the same `updating` flag + `lastSaveAt` timestamp so the
    // V3AutosaveIndicator reflects a BOM-only edit ("Saving…" → "Auto-saved
    // just now") without needing a separate badge or piggy-backing on the
    // project PUT (which doesn't run unless liteDirty is flipped).
    [LiteProjectActionTypes.SAVE_LITE_BOM_REQUEST](state: LiteProjectState): LiteProjectState {
      return { ...state, updating: true, error: false };
    },
    [LiteProjectActionTypes.SAVE_LITE_BOM_SUCCESS](state: LiteProjectState): LiteProjectState {
      return { ...state, updating: false, lastSaveAt: Date.now() };
    },
    [LiteProjectActionTypes.SAVE_LITE_BOM_FAILURE](state: LiteProjectState): LiteProjectState {
      return { ...state, updating: false, error: true };
    },
    // BOM refresh window — see `bomRefreshing` doc above.
    [LiteProjectActionTypes.FETCH_LITE_BOM_REQUEST](state: LiteProjectState): LiteProjectState {
      return { ...state, bomRefreshing: true };
    },
    [BomActionTypes.FETCH_BOM_PART_LIST_SUCCESS](state: LiteProjectState): LiteProjectState {
      return { ...state, bomRefreshing: false };
    },
    [BomActionTypes.FETCH_BOM_PART_LIST_ERROR](state: LiteProjectState): LiteProjectState {
      return { ...state, bomRefreshing: false };
    },
    // The autosave epic dispatches BOM_RESET (in place of FETCH_LITE_BOM_REQUEST)
    // when the project has zero panels, so the loader must clear here too —
    // otherwise the post-Clear-All overlay would hang with no GET to resolve it.
    [BomActionTypes.BOM_RESET](state: LiteProjectState): LiteProjectState {
      return { ...state, bomRefreshing: false };
    },
  }),
};
